export type EAStatus = 'not_started' | 'configure_in_progress' | 'analytics_ready' | 'report_ready';
export type ConfigureStatus = 'pending' | 'in_progress' | 'validated' | 'invalid';
export type AnalyticsStatus = 'none' | 'generating' | 'completed' | 'error';
export type ReportStatus = 'none' | 'generating' | 'completed' | 'error';
export type DriverDetectionStatus = 'none' | 'detecting' | 'completed' | 'error';
export type ReadinessLevel = 'low' | 'medium' | 'high';
export type Classification = 'verified' | 'derived' | 'modelled' | 'missing';
export type SourceType = 'user_input' | 'public_research' | 'uploaded_document' | 'existing_heymakr_research' | 'calculated' | 'inferred';
export type ValueType = 'text' | 'number' | 'percentage' | 'currency' | 'boolean' | 'date' | 'json';
export type FieldCategory = 'company' | 'financial' | 'vendor' | 'impact_revenue' | 'impact_cost' | 'impact_risk' | 'impact_compliance' | 'impact_productivity' | 'impact_infrastructure' | 'operational' | 'timeline' | 'kpi';
export type Confidence = 'high' | 'medium' | 'low';

export type BenefitCategory = 'hard_savings' | 'soft_savings' | 'revenue_upside' | 'risk_avoidance';

export type CurrencyCode = 'USD' | 'GBP' | 'EUR' | 'AUD' | 'CAD' | 'CHF' | 'JPY' | 'INR' | 'SGD' | 'HKD' | 'NZD' | 'SEK' | 'NOK' | 'DKK' | 'ZAR' | 'AED' | 'BRL' | 'MXN';

export interface BenefitBreakdown {
  hard_savings: number;
  soft_savings: number;
  revenue_upside: number;
  risk_avoidance: number;
  total_modelled_value: number;
  ebitda_relevant: number;
}

export interface ModelGuardrail {
  code: string;
  message: string;
  severity: 'warning' | 'critical';
  category: string;
}

export type ValueDriverType =
  | 'revenue_growth'
  | 'cost_efficiency'
  | 'risk_reduction'
  | 'compliance_regulatory'
  | 'productivity_workforce'
  | 'infrastructure_performance'
  | 'transformation_strategic';

export const VALUE_DRIVER_LABELS: Record<ValueDriverType, string> = {
  revenue_growth: 'Revenue Growth',
  cost_efficiency: 'Cost Efficiency',
  risk_reduction: 'Risk Reduction',
  compliance_regulatory: 'Compliance / Regulatory',
  productivity_workforce: 'Productivity / Workforce',
  infrastructure_performance: 'Infrastructure / Performance',
  transformation_strategic: 'Transformation / Strategic',
};

export interface EAWorkspaceValueDriver {
  id: string;
  workspace_id: string;
  driver_type: ValueDriverType;
  confidence_score: number;
  reasoning: string;
  is_primary: boolean;
  is_user_enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ValueDriverContext {
  vendor_summary: string;
  vertical_cfo_priorities: string[];
  account_pain_points: string[];
  primary_financial_levers: string[];
  deal_narrative: string;
}
export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type Likelihood = 'very_likely' | 'likely' | 'possible' | 'unlikely';

export type Recommendation =
  | 'strong_proceed'
  | 'proceed_with_conditions'
  | 'needs_more_validation'
  | 'weak_case'
  | 'do_not_present';

export type ReportStyle =
  | 'executive_brief'
  | 'cfo_focused'
  | 'cfo_cto_joint'
  | 'board_summary'
  | 'concise'
  | 'full_detailed';

export type ResearchStatus = 'none' | 'researching' | 'completed' | 'error';

export interface EAWorkspace {
  id: string;
  account_id: string;
  org_id: string;
  status: EAStatus;
  configure_status: ConfigureStatus;
  analytics_status: AnalyticsStatus;
  report_status: ReportStatus;
  driver_detection_status: DriverDetectionStatus;
  research_status: ResearchStatus;
  readiness_level: ReadinessLevel;
  completeness_score: number;
  confidence_score: number;
  deal_quality_score: number;
  recommendation: string;
  recommendation_rationale: string;
  currency: CurrencyCode;
  value_driver_context: ValueDriverContext | null;
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface EAConfigureSession {
  id: string;
  workspace_id: string;
  field_snapshot: Record<string, unknown>;
  validation_results: Record<string, unknown>;
  completeness_score: number;
  confidence_score: number;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface EAInputField {
  id: string;
  workspace_id: string;
  field_key: string;
  label: string;
  value: string;
  value_type: ValueType;
  source_type: SourceType;
  source_reference: string;
  confidence_score: number;
  classification: Classification;
  is_editable: boolean;
  is_required: boolean;
  used_in_model: boolean;
  validation_status: 'valid' | 'warning' | 'error' | 'unvalidated';
  category: FieldCategory;
  note: string;
  value_drivers: ValueDriverType[];
  help_context: string;
  description: string;
  why_it_matters: string;
  suggested_question: string;
  is_research_populated: boolean;
  user_validated: boolean;
  last_updated: string;
  created_at: string;
}

export interface EAUploadedDocument {
  id: string;
  workspace_id: string;
  file_name: string;
  storage_path: string;
  file_size: number;
  file_type: string;
  upload_status: 'uploaded' | 'extracting' | 'extracted' | 'error';
  extracted_text: string;
  parsed_facts: ParsedFact[];
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface ParsedFact {
  key: string;
  value: string;
  category: string;
  confidence: Confidence;
  source_context: string;
}

export interface EAResearchFinding {
  id: string;
  workspace_id: string;
  category: string;
  finding: string;
  source_type: string;
  source_label: string;
  confidence: Confidence;
  is_relevant: boolean;
  consumed_downstream: boolean;
  timestamp_ref: string | null;
  created_at: string;
}

export interface EASmartQuestion {
  id: string;
  workspace_id: string;
  question_text: string;
  question_group: string;
  linked_field_key: string;
  status: 'pending' | 'answered' | 'skipped' | 'suppressed';
  is_material: boolean;
  sort_order: number;
  answer_value: string;
  help_text: string;
  created_at: string;
  updated_at: string;
}

export interface EAAnalyticsSnapshot {
  id: string;
  workspace_id: string;
  version: number;
  active_value_drivers: ValueDriverType[];
  financial_outputs: FinancialOutputs;
  scenario_outputs: ScenarioOutputs;
  sensitivity_outputs: SensitivityOutput[];
  scores: EAScores;
  strategic_alignment: StrategicAlignmentOutput;
  execution_feasibility: ExecutionFeasibilityOutput;
  risks: RiskItem[];
  value_driver_tree: ValueDriverNode[];
  value_realisation_plan: MilestoneItem[];
  reasoning: Record<string, string>;
  recommendation: Recommendation | '';
  recommendation_rationale: string;
  created_at: string;
}

export interface FinancialOutputs {
  revenue_impact: number;
  cost_savings: number;
  labour_savings: number;
  risk_savings: number;
  compliance_savings: number;
  downtime_savings: number;
  total_annual_gross_benefit: number;
  total_annual_recurring_cost: number;
  total_one_time_cost: number;
  annual_net_benefit: number;
  adjusted_year_1_net_benefit: number;
  cash_flows: CashFlowYear[];
  roi_percent: number;
  annualised_roi_percent: number;
  irr: number | null;
  risk_adjusted_npv: number | null;
  tco: number;
  bcr: number | null;
  payback_months: number | null;
  npv: number | null;
  ebitda_impact: number;
  margin_improvement: number | null;
  ev_impact: number | null;
  total_cost_over_term: number;
  total_benefit_over_term: number;
  net_value_over_term: number;
  workforce_productivity_savings: number;
  benefit_breakdown: BenefitBreakdown;
  guardrails: ModelGuardrail[];
  formulas: FormulaRecord[];
}

export interface CashFlowYear {
  year: number;
  gross_benefit: number;
  recurring_cost: number;
  one_time_cost: number;
  net: number;
  cumulative: number;
}

export interface FormulaRecord {
  metric: string;
  formula: string;
  inputs: string[];
  result: number | null;
  classification: Classification;
}

export interface ScenarioOutputs {
  downside: ScenarioResult;
  base: ScenarioResult;
  upside: ScenarioResult;
}

export interface ScenarioResult {
  label: string;
  assumptions: Record<string, number>;
  annual_gross_benefit: number;
  annual_net_benefit: number;
  roi_percent: number;
  annualised_roi_percent: number;
  irr: number | null;
  payback_months: number | null;
  npv: number | null;
  ebitda_impact: number;
  ev_impact: number | null;
}

export interface SensitivityOutput {
  variable: string;
  label: string;
  base_value: number;
  low_value: number;
  high_value: number;
  roi_at_low: number;
  roi_at_high: number;
  delta_roi: number;
  npv_at_low: number | null;
  npv_at_high: number | null;
}

export interface EAScores {
  data_completeness: number;
  confidence: number;
  strategic_alignment: number;
  implementation_feasibility: number;
  roi_score: number;
  payback_score: number;
  deal_quality: number;
  risk_adjustment: number;
}

export interface StrategicAlignmentItem {
  objective: string;
  evidence: string;
  alignment_score: number;
  reasoning: string;
  confidence: Confidence;
}

export interface StrategicAlignmentOutput {
  items: StrategicAlignmentItem[];
  overall_score: number;
}

export interface ExecutionFeasibilityOutput {
  overall_score: number;
  dimensions: FeasibilityDimension[];
}

export interface FeasibilityDimension {
  name: string;
  score: number;
  reasoning: string;
}

export interface RiskItem {
  category: string;
  description: string;
  severity: Severity;
  likelihood: Likelihood;
  mitigation: string;
  confidence: Confidence;
  owner_suggestion: string;
}

export interface ValueDriverNode {
  capability: string;
  operational_change: string;
  kpi: string;
  financial_impact: string;
  estimated_value: number | null;
}

export interface MilestoneItem {
  objective: string;
  milestone_name: string;
  required_action: string;
  kpi_name: string;
  kpi_baseline: string;
  kpi_target: string;
  measurement_method: string;
  due_period: string;
  primary_owner: string;
  supporting_owners: string;
  dependencies: string;
  risk_if_missed: string;
  sort_order: number;
}

export interface EAReportDraft {
  id: string;
  workspace_id: string;
  snapshot_id: string | null;
  executive_first_name: string;
  executive_last_name: string;
  executive_title: string;
  company_name: string;
  subtitle: string;
  cover_note: string;
  author_name: string;
  report_date: string;
  report_style: ReportStyle;
  generated_sections: Record<string, string>;
  pdf_storage_path: string;
  generation_status: 'none' | 'generating' | 'completed' | 'error';
  error_message: string;
  created_at: string;
  updated_at: string;
}

export interface EAAccountListItem {
  id: string;
  company_name: string;
  website_url: string;
  logo_url: string;
  industry: string;
  employee_count: number;
  is_public_company: boolean | null;
  enrichment_status: string;
  ticker_symbol: string;
  updated_at: string;
  workspace?: EAWorkspace | null;
  intelligence_completeness: number;
}
